// Global JavaScript functionality
class Theme {
  constructor() {
    this.initializeComponents();
    this.bindEvents();
  }

  initializeComponents() {
    // Initialize cart functionality
    this.cart = new CartManager();
    
    // Initialize product forms
    this.initProductForms();
    
    // Initialize quantity selectors
    this.initQuantitySelectors();
    
    // Initialize mobile menu
    this.initMobileMenu();
  }

  bindEvents() {
    // Handle variant changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[name="id"]')) {
        this.handleVariantChange(e.target);
      }
    });

    // Handle quantity changes
    document.addEventListener('click', (e) => {
      if (e.target.matches('.quantity-button')) {
        this.handleQuantityChange(e.target);
      }
    });

    // Handle add to cart
    document.addEventListener('submit', (e) => {
      if (e.target.matches('.product-form')) {
        this.handleAddToCart(e);
      }
    });
  }

  initProductForms() {
    const forms = document.querySelectorAll('.product-form');
    forms.forEach(form => {
      const submitButton = form.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.addEventListener('click', (e) => {
          this.handleAddToCart(e);
        });
      }
    });
  }

  initQuantitySelectors() {
    const quantitySelectors = document.querySelectorAll('.quantity-selector');
    quantitySelectors.forEach(selector => {
      const minusBtn = selector.querySelector('.quantity-minus');
      const plusBtn = selector.querySelector('.quantity-plus');
      const input = selector.querySelector('.quantity-input');

      if (minusBtn) {
        minusBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value);
          if (currentValue > 1) {
            input.value = currentValue - 1;
            input.dispatchEvent(new Event('change'));
          }
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value);
          input.value = currentValue + 1;
          input.dispatchEvent(new Event('change'));
        });
      }
    });
  }

  initMobileMenu() {
    const mobileMenuToggle = document.querySelector('.mobile-menu-toggle');
    const mobileMenu = document.querySelector('.mobile-menu');
    
    if (mobileMenuToggle && mobileMenu) {
      mobileMenuToggle.addEventListener('click', () => {
        mobileMenu.classList.toggle('is-open');
      });
    }
  }

  handleVariantChange(select) {
    const form = select.closest('form');
    const submitButton = form.querySelector('[type="submit"]');
    const priceElement = form.querySelector('.price');
    
    // Update price and availability
    const selectedOption = select.options[select.selectedIndex];
    const price = selectedOption.dataset.price;
    const available = selectedOption.dataset.available === 'true';
    
    if (priceElement && price) {
      priceElement.textContent = price;
    }
    
    if (submitButton) {
      submitButton.disabled = !available;
      submitButton.textContent = available ? 'Ajouter au panier' : 'Rupture de stock';
    }
  }

  handleQuantityChange(button) {
    const quantityInput = button.parentNode.querySelector('input[type="number"]');
    const isIncrement = button.classList.contains('quantity-plus');
    const currentValue = parseInt(quantityInput.value);
    
    if (isIncrement) {
      quantityInput.value = currentValue + 1;
    } else if (currentValue > 1) {
      quantityInput.value = currentValue - 1;
    }
    
    quantityInput.dispatchEvent(new Event('change'));
  }

  async handleAddToCart(e) {
    e.preventDefault();
    
    const form = e.target.closest('form');
    const submitButton = form.querySelector('[type="submit"]');
    const formData = new FormData(form);
    
    // Show loading state
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Ajout en cours...';
    submitButton.disabled = true;
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const item = await response.json();
        this.cart.updateCartCount();
        this.showNotification('Produit ajouté au panier !', 'success');
      } else {
        throw new Error('Erreur lors de l\'ajout au panier');
      }
    } catch (error) {
      this.showNotification('Erreur lors de l\'ajout au panier', 'error');
    } finally {
      // Reset button state
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Trigger animation
    setTimeout(() => {
      notification.classList.add('notification--show');
    }, 100);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.remove('notification--show');
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  }
}

// Cart Manager Class
class CartManager {
  constructor() {
    this.init();
  }

  init() {
    this.updateCartCount();
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartCountElements = document.querySelectorAll('.cart-count');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du panier:', error);
    }
  }

  async addItem(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.updateCartCount();
        return await response.json();
      } else {
        throw new Error('Erreur lors de l\'ajout au panier');
      }
    } catch (error) {
      console.error('Erreur:', error);
      throw error;
    }
  }
}

// Utility functions
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function throttle(func, delay) {
  let timeoutId;
  let lastExecTime = 0;
  return function (...args) {
    const currentTime = Date.now();
    
    if (currentTime - lastExecTime > delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  };
}

// Initialize theme when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new Theme();
});

// Add notification styles
const notificationStyles = `
  .notification {
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    color: white;
    font-weight: 600;
    z-index: 9999;
    transform: translateX(100%);
    opacity: 0;
    transition: all 0.3s ease;
  }
  
  .notification--show {
    transform: translateX(0);
    opacity: 1;
  }
  
  .notification--success {
    background-color: #10b981;
  }
  
  .notification--error {
    background-color: #ef4444;
  }
  
  .notification--info {
    background-color: #3b82f6;
  }
`;

// Inject notification styles
const styleSheet = document.createElement('style');
styleSheet.textContent = notificationStyles;
document.head.appendChild(styleSheet);